import { z } from 'zod';
import { ROLES } from '../enums.js';
import type { Permission } from '../permissions.js';

// Request bodies for /api/auth and /api/users. The API validates with these and the web
// app uses the same ones in its forms, so the two can't disagree.

export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;

/** New passwords. (Existing ones from the old system may be shorter; they still work.) */
export const newPassword = z
  .string()
  .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters`)
  .max(PASSWORD_MAX, `Use at most ${PASSWORD_MAX} characters`);

export const loginBody = z.object({
  /** Username or email, as before. */
  login: z.string().trim().min(1, 'Enter your username or email').max(254),
  password: z.string().min(1, 'Enter your password').max(PASSWORD_MAX),
});
export type LoginBody = z.infer<typeof loginBody>;

/** The second step of signing in: the 6-digit code from their authenticator app. */
export const mfaCodeBody = z.object({
  /** From the first step (password), valid for a few minutes. */
  ticket: z.string().min(1).max(2000),
  code: z
    .string()
    .transform((v) => v.replace(/\s+/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app')),
  /** Skip the code on this device for 90 days. */
  remember: z.boolean().default(true),
});
export type MfaCodeBody = z.input<typeof mfaCodeBody>;

/** How long "Remember this device" lasts. */
export const TRUSTED_DEVICE_DAYS = 90;

/**
 * What signing in with a password returns: signed in (Me), or the code is needed next. The
 * first time, the app has to be set up: the secret and its QR code link come with it.
 */
export type LoginResult =
  | (Me & { mfa?: undefined })
  | { mfa: 'verify'; ticket: string }
  | { mfa: 'setup'; ticket: string; secret: string; otpauthUrl: string; account: string };

export const forgotPasswordBody = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address').max(254),
});

export const resetPasswordBody = z.object({
  token: z.string().regex(/^[a-f0-9]{64}$/, 'This reset link is not valid'),
  password: newPassword,
});

export const changePasswordBody = z.object({
  currentPassword: z.string().min(1).max(PASSWORD_MAX),
  newPassword,
});

const email = z.string().trim().toLowerCase().email('Enter a valid email address').max(254);
const username = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[A-Za-z0-9._-]+$/, 'Letters, numbers, dots, dashes and underscores only');

export const createUserBody = z.object({
  email,
  username: username.optional(),
  displayName: z.string().trim().min(1).max(128),
  role: z.enum(ROLES),
  /** Ignored for admins, who see every organisation. */
  organisationIds: z.array(z.string().uuid()).max(500).default([]),
  /** Leave out to have one generated and emailed. */
  password: newPassword.optional(),
  sendWelcomeEmail: z.boolean().default(true),
});
export type CreateUserBody = z.infer<typeof createUserBody>;

export const updateUserBody = z
  .object({
    email,
    username: username.nullable(),
    displayName: z.string().trim().min(1).max(128),
    role: z.enum(ROLES),
    organisationIds: z.array(z.string().uuid()).max(500),
    isActive: z.boolean(),
  })
  .partial();
export type UpdateUserBody = z.infer<typeof updateUserBody>;

/** What /api/auth/me returns: who is signed in and what they may do. */
export interface Me {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  role: (typeof ROLES)[number];
  /** null for admins (every organisation). */
  organisationIds: string[] | null;
  permissions: Permission[];
  mustChangePassword: boolean;
}

/** Error body for every failed request. */
export interface ApiError {
  error: string;
  /** Field problems from validation, keyed by field path, e.g. { "email": "Enter a valid email address" }. */
  fields?: Record<string, string>;
}
